/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogTitle, Button, Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { getCurrentTimeInTimezone, toISOStringWithTimezone } from "../../../lib/Util";
import { CSVLink } from "react-csv";
import { getAlarmHistory } from "../../../lib/Queries";
import { LoadingButton } from "@mui/lab";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const headers = [
  { label: "Severity", key: "alarm_type" },
  { label: "Start Time", key: "timestamp" },
  { label: "Resolved Time", key: "stop_time" },
  { label: "Alarm Name", key: "description" },
  { label: "Asset", key: "type" },
  { label: "Alarm Source", key: "source" },
  { label: "Site Name", key: "site_name" },
];

const addSiteName = (alarms, siteName) => {
  return alarms.map((alarm) => {
    return { ...alarm, site_name: siteName };
  });
};

const AlarmsDownloadDialog = ({ start, stop, open, onClose, siteUuid, siteName, timezone }) => {
  const [requestStart, setRequestStart] = useState(start);
  const [requestStop, setRequestStop] = useState(stop);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const handleClose = () => {
    setData(null);
    onClose();
  };

  const CSVDownload = (props) => {
    const btnRef = useRef(null);
    useEffect(() => {
      btnRef.current?.click();
      handleClose();
    }, [btnRef]);
    return (
      <CSVLink {...props}>
        <span ref={btnRef} />
      </CSVLink>
    );
  };

  const getHistoricalStart = () => {
    const startDate = "2010-01-01T00:00:00";
    const historicalStartTimezoneFormatted = toISOStringWithTimezone(new Date(startDate), timezone);
    return historicalStartTimezoneFormatted;
  };

  const getHistoricalStop = () => {
    const historicalStop = getCurrentTimeInTimezone(timezone);
    return historicalStop;
  };

  const isInvalidRange =
    !!requestStart &&
    !!requestStop &&
    new Date(requestStart).getTime() >= new Date(requestStop).getTime();

  const fetchData = async () => {
    if (!requestStart || !requestStop) return;
    if (isInvalidRange) return;
    setLoading(true);
    try {
      const response = await getAlarmHistory(siteUuid, requestStart, requestStop);
      const alarms = response["historical_alerts"];
      const alarmsWithSiteName = addSiteName(alarms, siteName);
      setData(alarmsWithSiteName);
    } catch (error) {
      console.error("Error downloading alarms data: ", error);
    }
    setLoading(false);
  };

  const downloadAllHistorical = async () => {
    setLoading(true);
    try {
      const startStr = getHistoricalStart();
      const stopStr = getHistoricalStop();
      const response = await getAlarmHistory(siteUuid, startStr, stopStr);
      setLoading(false);
      const alarms = response["historical_alerts"];
      const alarmsWithSiteName = addSiteName(alarms, siteName);
      setData(alarmsWithSiteName);
    } catch (error) {
      console.error("Error downloading historical alarms data: ", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    setRequestStart(start);
    setRequestStop(stop);
  }, [start, stop, open, timezone]);

  const handleStartChange = (newValue) => {
    if (!newValue) return;
    setRequestStart(newValue.tz(timezone).format("YYYY-MM-DDTHH:mm:ssZ"));
  };

  const handleStopChange = (newValue) => {
    if (!newValue) return;
    setRequestStop(newValue.tz(timezone).format("YYYY-MM-DDTHH:mm:ssZ"));
  };

  const getDurationText = () => {
    if (!requestStart || !requestStop) return "";
    const diff = Math.max(0, new Date(requestStop).getTime() - new Date(requestStart).getTime());
    const dayMs = 24 * 60 * 60 * 1000;
    const days = Math.floor(diff / dayMs);
    const minutes = Math.floor((diff % dayMs) / (60 * 1000));
    return `${days} days, ${minutes} minutes`;
  };

  return (
    <Dialog open={open}>
      <Box sx={{ px: "2.25rem", py: "2.5rem" }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Download Alarms Data
        </Typography>
        {/* Time Range Pickers */}
        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" alignItems="center">
              <Box mr={1}>
                <DateTimePicker
                  label="Time Range Start"
                  value={requestStart ? dayjs(requestStart) : null}
                  onChange={handleStartChange}
                  timezone={timezone}
                  format="MM/DD/YYYY hh:mm A"
                  slotProps={{
                    textField: {
                      size: "small",
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ mx: 1 }}>
                -
              </Typography>
              <Box ml={1}>
                <DateTimePicker
                  label="Time Range End"
                  value={requestStop ? dayjs(requestStop) : null}
                  onChange={handleStopChange}
                  timezone={timezone}
                  format="MM/DD/YYYY hh:mm A"
                  slotProps={{
                    textField: {
                      size: "small",
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </Box>
            </Box>
          </LocalizationProvider>
        </Box>
        <Typography variant="body2" sx={{ mb: 4 }}>
          {getDurationText()}
        </Typography>
        <Box display="flex" flexDirection="row" width="100%" justifyContent="end">
          <Button
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            sx={{ marginLeft: "1em", width: "100px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={downloadAllHistorical}
            disabled={loading}
            variant="outlined"
            color="inherit"
            sx={{ marginLeft: "1em", width: "200px" }}
          >
            Download All Historical
          </Button>
          <LoadingButton
            loading={loading}
            onClick={fetchData}
            variant="contained"
            sx={{ marginLeft: "1em", width: "200px" }}
            disabled={isInvalidRange}
          >
            Download Time Range
          </LoadingButton>
        </Box>
        {data ? (
          <CSVDownload
            data={data}
            target="_blank"
            filename={`scaleos-alarms-export-${siteName}-${new Date().toISOString()}.csv`}
            headers={headers}
          />
        ) : null}
      </Box>
    </Dialog>
  );
};

AlarmsDownloadDialog.propTypes = {
  start: PropTypes.string,
  stop: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  siteUuid: PropTypes.string,
  siteName: PropTypes.string,
  timezone: PropTypes.string,
};
export default AlarmsDownloadDialog;
